import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { User, UserManager } from "oidc-client-ts";
import { asError, eventBus } from "@optimyl/events";
import { currentPage, signInAgain, startSignIn, startSignOut } from "./identityRedirect";

export interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** False when the app runs without an identity server (a local run): nothing needs a sign-in. */
  isEnabled: boolean;
  login: (forcePrompt?: boolean) => Promise<void>;
  /** Signs out of every Optimyl app in this browser; `before` (releasing the form lock) runs once the tab is marked as leaving. */
  signOut: (before?: () => Promise<void>) => Promise<void>;
  getAccessToken: () => string | null;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export interface SessionProviderProps {
  /** The app's user manager, or null when sign-in is turned off. */
  userManager: UserManager | null;
  identityUrl: string;
  clientId: string;
  /** What every sign-in carries. Keep it the same function across renders (createAuth does). */
  getSignInParams: () => Record<string, string>;
  children: ReactNode;
}

// The error a refused refresh token comes back as. By name, not instanceof: an app with its own copy of oidc-client-ts
// would otherwise never match, and a refused renewal would leave a dead tab.
function isRefusedGrant(error: Error): boolean {
  return error.name === "ErrorResponse" && (error as Error & { error?: string }).error === "invalid_grant";
}

/**
 * The signed-in session: who is signed in, and sign-in and sign-out that behave the same in every app. A refused refresh
 * token (the session ended or was signed out, or two-step verification must be set up first) starts a sign-in straight
 * away rather than leaving a dead tab until the next request's 401. Built by createAuth.
 */
export function SessionProvider({ userManager, identityUrl, clientId, getSignInParams, children }: SessionProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  // The person the page shows, for the pageshow handler below.
  const shownUserRef = useRef<User | null>(null);
  useEffect(() => {
    shownUserRef.current = user;
  }, [user]);
  // When sign-in is turned off there is nothing to load.
  const [isLoading, setIsLoading] = useState(userManager !== null);

  useEffect(() => {
    if (!userManager) return;

    userManager.getUser()
      .then((existingUser) => {
        if (existingUser && !existingUser.expired) {
          setUser(existingUser);
        }
      })
      .catch((err: unknown) => {
        eventBus.publish("error", { error: asError(err), context: "AuthProvider.getUser" });
      })
      .finally(() => setIsLoading(false));

    const onUserLoaded = (loadedUser: User) => setUser(loadedUser);
    const onUserUnloaded = () => setUser(null);
    const onSilentRenewError = (error: Error) => {
      eventBus.publish("error", { error, context: "oidc.silentRenew" });
      if (isRefusedGrant(error)) {
        signInAgain(userManager, "oidc.silentRenew.signIn", getSignInParams());
      }
    };

    // Back after Sign out: the browser restores the page as it was, still showing the person. The stored session is
    // gone by then (startSignOut cleared it), so a restored page that showed someone, with no stored session, starts
    // again from the server. A page that showed no one has nothing stale to hide, and a session that merely expired is
    // the silent renew's to refresh.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || !shownUserRef.current) return;
      userManager.getUser()
        .then((storedUser) => {
          if (!storedUser) window.location.reload();
        })
        .catch((err: unknown) => {
          eventBus.publish("error", { error: asError(err), context: "AuthProvider.pageshow" });
        });
    };

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addSilentRenewError(onSilentRenewError);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [userManager, getSignInParams]);

  const login = useCallback((forcePrompt = false) => {
    if (!userManager) return Promise.resolve();
    return startSignIn(userManager, { forcePrompt, returnTo: currentPage() }, getSignInParams());
  }, [userManager, getSignInParams]);

  // A plain redirect to the identity server's sign-out, not oidc-client-ts signoutRedirect() (unreliable on Safari).
  const signOut = useCallback(
    (before?: () => Promise<void>) => startSignOut(userManager, identityUrl, clientId, before),
    [userManager, identityUrl, clientId],
  );

  const getAccessToken = useCallback(() => user?.access_token ?? null, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: !!user && !user.expired, isLoading, isEnabled: userManager !== null, login, signOut, getAccessToken }),
    [user, isLoading, userManager, login, signOut, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
